/**
 * Step 1: Source Selection
 *
 * Select payroll source type and upload file.
 *
 * Session 4.1: Wizard State Machine + Steps 1-2
 */

import { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import {
  DocumentArrowUpIcon,
  XMarkIcon,
  ChartBarIcon,
  DocumentTextIcon,
  PencilIcon,
} from '@heroicons/react/24/outline';
import {
  CheckCircleIcon,
  ExclamationCircleIcon,
} from '@heroicons/react/24/solid';
import { useWizard } from '../../context/WizardContext';
import { WizardNavigation } from '../WizardNavigation';
import { SOURCE_CONFIGS, type SourceType } from '../../context/types';
import { wizardApi } from '../../services/wizardApi';

/**
 * Get icon component for source type.
 */
function getSourceIcon(type: SourceType) {
  switch (type) {
    case 'quickbooks':
      return ChartBarIcon;
    case 'csv':
      return DocumentTextIcon;
    case 'manual':
      return PencilIcon;
  }
}

/**
 * Step 1: Select source type and upload file.
 */
export function Step1Source() {
  const {
    state,
    setSourceType,
    setUploadedFile,
    setParseResult,
    setError,
    nextStep,
  } = useWizard();

  const [isParsing, setIsParsing] = useState(false);

  // Handle file drop
  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      const file = acceptedFiles[0];
      if (!file) return;

      setUploadedFile(file);
      setError(null);

      // If not manual entry, parse the file
      if (state.sourceType && state.sourceType !== 'manual') {
        setIsParsing(true);
        try {
          const result = await wizardApi.parseFile(file, state.sourceType);
          setParseResult(result);

          if (!result.success) {
            setError(result.errors[0] || 'Failed to parse file');
          }
        } catch (err) {
          setError(err instanceof Error ? err.message : 'Failed to parse file');
        } finally {
          setIsParsing(false);
        }
      }
    },
    [state.sourceType, setUploadedFile, setParseResult, setError]
  );

  // Get accepted extensions for current source
  const acceptedExtensions = state.sourceType
    ? SOURCE_CONFIGS[state.sourceType].acceptedExtensions
    : [];

  // Build accept object for dropzone
  const acceptConfig = acceptedExtensions.reduce(
    (acc, ext) => {
      if (ext === '.csv') acc['text/csv'] = ['.csv'];
      if (ext === '.xlsx') {
        acc[
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        ] = ['.xlsx'];
      }
      if (ext === '.xls') acc['application/vnd.ms-excel'] = ['.xls'];
      return acc;
    },
    {} as Record<string, string[]>
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: acceptConfig,
    maxFiles: 1,
    disabled: !state.sourceType || state.sourceType === 'manual',
  });

  // Handle source type selection
  const handleSourceSelect = (type: SourceType) => {
    setSourceType(type);
  };

  // Handle removing uploaded file
  const handleRemoveFile = () => {
    setUploadedFile(null);
  };

  // Handle next button
  const handleNext = async () => {
    if (state.sourceType === 'manual') {
      nextStep();
    } else if (state.parseResult?.success) {
      nextStep();
    }
  };

  return (
    <div>
      <h2 className="text-lg font-medium text-gray-900 mb-6">
        Select Your Payroll Source
      </h2>

      {/* Source type selection */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        {Object.values(SOURCE_CONFIGS).map(config => {
          const Icon = getSourceIcon(config.type);
          const isSelected = state.sourceType === config.type;

          return (
            <button
              key={config.type}
              onClick={() => handleSourceSelect(config.type)}
              type="button"
              className={`
                relative p-6 rounded-lg border-2 text-left transition-colors
                ${
                  isSelected
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                }
              `}
            >
              <Icon className="h-8 w-8 text-gray-600 mb-3" />
              <h3 className="text-sm font-medium text-gray-900">
                {config.label}
              </h3>
              <p className="mt-1 text-xs text-gray-500">{config.description}</p>

              {/* Selection indicator */}
              {isSelected && (
                <div className="absolute top-3 right-3 w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center">
                  <svg
                    className="w-3 h-3 text-white"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
              )}
            </button>
          );
        })}
      </div>

      {/* File upload (if not manual) */}
      {state.sourceType && state.sourceType !== 'manual' && (
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Upload Your File
          </label>

          {!state.uploadedFile ? (
            <div
              {...getRootProps()}
              className={`
                border-2 border-dashed rounded-lg p-8 text-center cursor-pointer
                transition-colors
                ${
                  isDragActive
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-300 hover:border-gray-400'
                }
              `}
            >
              <input {...getInputProps()} />
              <DocumentArrowUpIcon className="mx-auto h-12 w-12 text-gray-400" />
              <p className="mt-2 text-sm text-gray-600">
                {isDragActive
                  ? 'Drop your file here...'
                  : 'Drag and drop your file here, or click to select'}
              </p>
              <p className="mt-1 text-xs text-gray-500">
                Accepted: {acceptedExtensions.join(', ')}
              </p>
            </div>
          ) : (
            <div className="border rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-gray-100 rounded">
                    <DocumentArrowUpIcon className="h-6 w-6 text-gray-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      {state.uploadedFile.name}
                    </p>
                    <p className="text-xs text-gray-500">
                      {(state.uploadedFile.size / 1024).toFixed(1)} KB
                    </p>
                  </div>
                </div>

                <button
                  onClick={handleRemoveFile}
                  type="button"
                  className="p-1 text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>

              {/* Parse status */}
              {isParsing && (
                <div className="mt-4 flex items-center gap-2 text-sm text-gray-600">
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                      fill="none"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  Parsing file...
                </div>
              )}

              {state.parseResult && (
                <div className="mt-4">
                  {state.parseResult.success ? (
                    <div className="p-3 bg-green-50 rounded-lg">
                      <div className="flex items-center gap-2">
                        <CheckCircleIcon className="h-5 w-5 text-green-500" />
                        <p className="text-sm text-green-800">
                          Successfully parsed{' '}
                          {state.parseResult.employees.length} employees
                          {state.parseResult.confidence && (
                            <span className="text-green-600">
                              {' '}
                              (Confidence:{' '}
                              {Math.round(state.parseResult.confidence * 100)}%)
                            </span>
                          )}
                        </p>
                      </div>
                      {state.parseResult.warnings.length > 0 && (
                        <ul className="mt-2 text-xs text-amber-700 list-disc list-inside">
                          {state.parseResult.warnings.map((w, i) => (
                            <li key={i}>{w}</li>
                          ))}
                        </ul>
                      )}
                    </div>
                  ) : (
                    <div className="p-3 bg-red-50 rounded-lg">
                      <div className="flex items-center gap-2">
                        <ExclamationCircleIcon className="h-5 w-5 text-red-500" />
                        <p className="text-sm text-red-800">
                          Failed to parse file
                        </p>
                      </div>
                      <ul className="mt-1 text-xs text-red-700 list-disc list-inside">
                        {state.parseResult.errors.map((e, i) => (
                          <li key={i}>{e}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Manual entry info */}
      {state.sourceType === 'manual' && (
        <div className="mb-6 p-4 bg-blue-50 rounded-lg">
          <p className="text-sm text-blue-800">
            You&apos;ll enter employee data manually in step 3. Click Continue
            to configure the contribution period.
          </p>
        </div>
      )}

      {/* Navigation */}
      <WizardNavigation
        onNext={handleNext}
        isNextLoading={isParsing}
        showBack={false}
      />
    </div>
  );
}
